/**
 * Text component with Dashboard3 text color tokens
 *
 * Note: Must be "use client" because it uses useThemeTokens hook
 *
 * @example
 * ```tsx
 * <Text variant="primary">Main text</Text>
 * <Text variant="secondary">Secondary text</Text>
 * ```
 */

"use client";

import {
  Text as ChakraText,
  type TextProps as ChakraTextProps,
} from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface TextProps extends ChakraTextProps {
  /**
   * Text color variant based on Dashboard3 theme
   * - primary: Main text (92% opacity)
   * - secondary: Secondary text (68% opacity)
   * - tertiary: Tertiary text (45% opacity)
   * - muted: Muted text (28% opacity)
   */
  variant?: "primary" | "secondary" | "tertiary" | "muted";
}

export const Text = ({ variant = "primary", ...props }: TextProps) => {
  const theme = useThemeTokens();

  const colorMap = {
    primary: theme.text.primary,
    secondary: theme.text.secondary,
    tertiary: theme.text.tertiary,
    muted: theme.text.muted,
  };

  return <ChakraText color={colorMap[variant]} {...props} />;
};
